// apps/provider/src/hooks/useCalendarRealtimeSync.js
// Keeps calendarStore in sync with the org's realtime appointment channel.
// Shared by AppointmentCalendar and Home so any surface rendering from the
// store reflects other users' changes live:
// - status_change: in-place patch
// - created/updated: coverage-gated single stitched refetch (ingest)
// - deleted: removal (tombstoned against stale in-flight fetches)
// - reconnect: revalidate the visible range (Xano replays no missed messages)
// - visibilitychange backstop: revive dead sockets the SDK won't auto-reconnect
//   and refresh if the visible range went stale during the gap

import { useCallback, useEffect, useRef } from 'react';
import { useAppointmentRealtime } from './useAppointmentRealtime';
import { useCalendarStore } from '../stores/calendarStore';
import { useCalendarAvailabilityStore } from '../stores/calendarAvailabilityStore';
import { realtimeService } from '@/services/realtimeService';

// Random stagger for post-reconnect revalidation: when the instance recovers
// from a blip, every connected tab reconnects within seconds of each other —
// firing the full-range refetch immediately from all of them is a thundering
// herd that can re-trigger the very overload that dropped the sockets. The
// jitter spreads the identical total work across the window (~15x lower peak).
// 60s (was 30s, 2026-09-22): a stall long enough to drop sockets is followed by
// every tab's full-range refetch; at 40+ tabs the 30s window re-created the
// stall that dropped them.
const RECONNECT_JITTER_MAX_MS = 60000;
// Visible-again refresh spread (2026-09-22). Resumes were assumed to be
// per-user and decorrelated; at shift change a clinic's tabs come back
// together, so the forced refetch gets a small jitter too.
const VISIBLE_REFRESH_JITTER_MAX_MS = 10000;
// Hidden shorter than this: fetch the ids queued while hidden (cheap singles).
// Longer: drop them and revalidate the range once instead.
const HIDDEN_FLUSH_MAX_MS = 60000;

export function useCalendarRealtimeSync({ organizationId, enabled = true } = {}) {
  const reconnectTimerRef = useRef(null);
  const visibleTimerRef = useRef(null);
  const hiddenAtRef = useRef(null);
  const staleAfterReconnectRef = useRef(false);
  const { recentlyUpdated } = useAppointmentRealtime(
    organizationId,
    useCallback((event) => {
      const store = useCalendarStore.getState();
      // Check-in fields ride the status event (2026-09-22) so a check-in no
      // longer publishes a second "updated" event that every tab refetched
      const fields = { status: event.status, updated_at: event.updated_at };
      for (const key of ['checked_in_at', 'checked_in_by', 'encounter_id']) {
        if (event[key] !== undefined) fields[key] = event[key];
      }
      store.patchAppointment(event.appointment_id, fields);
      store.markRecentlyUpdated(event.appointment_id);
    }, []),
    {
      enabled,
      // Keep the org channel alive across route transitions — subscription
      // churn loses events published during the gap (no replay on Xano)
      persistent: true,
      onCreated: useCallback((event) => {
        const store = useCalendarStore.getState();
        // Only hydrate events landing inside covered ranges — anything else
        // is picked up by the normal fetch when the user navigates there
        if (store.isDateCovered(event.appointment_date)) {
          store.ingestAppointmentIds([event.appointment_id]);
          store.markRecentlyUpdated(event.appointment_id);
        }
      }, []),
      onUpdated: useCallback((event) => {
        const store = useCalendarStore.getState();
        if (store.isDateCovered(event.appointment_date)) {
          store.ingestAppointmentIds([event.appointment_id]);
          store.markRecentlyUpdated(event.appointment_id);
        } else if (store.appointmentsById[event.appointment_id]) {
          // Rescheduled out of every covered range — drop the stale copy
          store.removeAppointment(event.appointment_id);
        }
      }, []),
      onDeleted: useCallback((event) => {
        useCalendarStore.getState().removeAppointment(event.appointment_id);
      }, []),
      onOverrideEvent: useCallback((event) => {
        // Schedule-override change (block/note) — mark the provider dirty;
        // the store debounces and does bounded refetch work (never per-event)
        useCalendarAvailabilityStore.getState().ingestOverrideEvent(event);
      }, []),
      onReconnect: useCallback(() => {
        // No message replay on Xano channels — refresh the visible range,
        // jittered. A new reconnect during a pending timer RESETS it, so a
        // flapping connection yields one revalidation after stabilizing.
        // Availability/overrides share the same single timer and jitter
        // window — never a second synchronized refetch wave.
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          // Hidden tabs sit out the reconnect wave (2026-09-22): they mark
          // themselves stale and the visibility backstop refetches once they
          // are looked at. After an outage most of a clinic's tabs are hidden.
          if (document.visibilityState === 'hidden') {
            staleAfterReconnectRef.current = true;
            return;
          }
          useCalendarStore.getState().revalidateRange();
          if (useCalendarAvailabilityStore.getState().hasLoaded) {
            useCalendarAvailabilityStore.getState().refresh();
          }
        }, Math.random() * RECONNECT_JITTER_MAX_MS);
      }, []),
    }
  );

  // Clear any pending jittered revalidation on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (visibleTimerRef.current) {
        clearTimeout(visibleTimerRef.current);
        visibleTimerRef.current = null;
      }
    };
  }, []);

  // Backstop for laptop-sleep/dead-socket gaps the SDK won't auto-reconnect:
  // on tab visible, revive the socket, release what the hidden tab held
  // (queued ingest ids, dirty override providers), and refresh if the visible
  // range is stale. The refetches are jittered (see VISIBLE_REFRESH_JITTER_MAX_MS).
  useEffect(() => {
    if (!enabled) return;
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') {
        if (hiddenAtRef.current == null) hiddenAtRef.current = Date.now();
        return;
      }
      const hiddenMs = hiddenAtRef.current == null ? 0 : Date.now() - hiddenAtRef.current;
      hiddenAtRef.current = null;

      realtimeService.ensureConnected();
      const store = useCalendarStore.getState();
      const params = store.lastFetchParams;
      let needRevalidate = !!(params && (
        staleAfterReconnectRef.current ||
        !store.isRangeCovered(params.start, params.end, { maxAgeMs: 60000 })
      ));
      const reconnectedWhileHidden = staleAfterReconnectRef.current;
      staleAfterReconnectRef.current = false;
      if (store.hasPendingBurst()) {
        store.dropIngestQueue();
        needRevalidate = !!params;
      }
      if (store.pendingIngestCount() > 0) {
        // A range refetch supersedes the queued singles; only flush them when
        // the range is otherwise fresh and the absence was short
        if (needRevalidate || hiddenMs >= HIDDEN_FLUSH_MAX_MS) {
          store.dropIngestQueue();
          needRevalidate = !!params;
        } else {
          store.flushIngestQueue();
        }
      }
      const availStore = useCalendarAvailabilityStore.getState();
      availStore.flushOverrideQueue();
      const availStale = !!(availStore.hasLoaded && (
        reconnectedWhileHidden ||
        (availStore.fetchedAt && Date.now() - availStore.fetchedAt > 60000)
      ));

      if (!needRevalidate && !availStale) return;
      if (visibleTimerRef.current) clearTimeout(visibleTimerRef.current);
      visibleTimerRef.current = setTimeout(() => {
        visibleTimerRef.current = null;
        if (needRevalidate) useCalendarStore.getState().revalidateRange();
        // Overrides may have gone stale during the same gap (events lost while
        // the socket was dead)
        if (availStale) useCalendarAvailabilityStore.getState().refresh();
      }, Math.random() * VISIBLE_REFRESH_JITTER_MAX_MS);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [enabled]);

  return { recentlyUpdated };
}
